import React, { useMemo, useCallback, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";
import { diff } from "json8-patch";
import { FormikHelpers } from "formik";

import DisplayDateCreated from "../../../components/data-components/DisplayDateCreated";
import DisplayCreatedBy from "../../../components/data-components/DisplayCreatedBy";
import { EditDynamicProperties } from "../../../components/data-components/EditDynamicProperties";
import {
  DisplaySewerObjectId,
  DisplayDataOwner,
  DisplayInProject,
  DisplayOpenJobs,
} from "../components/DataComponents";
import { SubSection, DataSection } from "../../../components/data-components/sections/DataSections";
import PageLayout from "../../_layout/PageLayout";
import Form from "../../../components/form/Form";
import { SectionDynamicMenuItemsPages, SectionMenuItemsModel } from "./SectionMenuItems";
import {
  EditSewerObject,
  EditSewerObjectFromAssignedProject,
} from "../../../services/authorization/Permissions";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import useUrlParams from "../../../services/url/UrlParamsService";
import { CurrentSectionStorageModel } from "../../../services/sewer-object/CurrentSectionStorage";
import { api } from "../../../services/api/ApiService";
import { warningLeavePageService } from "../../../services/page/WarningLeavePageService";
import { navigatePath } from "../../../helpers/StringHelpers";
import useRouteService from "../../../services/route-service/RouteService";
import { EntityTypeFromPath } from "../../../services/breadcrumbs/EntityTypeFromPathModel";
import { breadcrumbsStorage } from "../../../services/breadcrumbs/BreadcrumbsStorage";
import { FieldsFromBM } from "../../../api-models/response/BluemetricFields";

export default observer(() => {
  const urlParamsService = useUrlParams();
  const history = useHistory();
  const { handleErrorInSnackbar } = useErrorHandler();
  const { isUserOnProjects } = useRouteService();

  const [currentSectionStorage] = useState(() =>
    CurrentSectionStorageModel.create({
      currentSectionId: urlParamsService.getSectionId()!,
      parentEntityFromPath: {
        id: urlParamsService.getSewerObjectParentId()!,
        type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job
      }
    }, { api })
  );

  const menuItems = useMemo(() => {
    const sectionMenuItems = SectionMenuItemsModel.create({
      sectionId: currentSectionStorage.currentSectionId,
      section: currentSectionStorage.currentSection,
      dynamicMenuItemPage: SectionDynamicMenuItemsPages.EditSection,
      parentIdFromPath: currentSectionStorage.parentEntityFromPath.id,
    });

    return {
      staticItems: sectionMenuItems.getStaticMenuItems(),
      dynamicItems: sectionMenuItems.getDynamicMenuItems(),
    };
  }, [currentSectionStorage.currentSectionId, currentSectionStorage.currentSection, currentSectionStorage.parentEntityFromPath.id]);

  const getSectionIsLockedWarning = useCallback(() => {
    if (
      currentSectionStorage.userWithLockOnSection &&
      !currentSectionStorage.currentUserHasLockOnSection
    )
      return {
        key: "EditSection.locked-inspection",
        context: { userName: currentSectionStorage.userWithLockOnSection.name },
      };
    else return undefined;
  }, [
    currentSectionStorage.userWithLockOnSection,
    currentSectionStorage.currentUserHasLockOnSection,
  ]);

  const canSubmitEdit = useMemo(
    () =>
      (!currentSectionStorage.sectionIsLocked ||
        currentSectionStorage.currentUserHasLockOnSection) &&
      currentSectionStorage.canUserEditSection,
    [
      currentSectionStorage.canUserEditSection,
      currentSectionStorage.currentUserHasLockOnSection,
      currentSectionStorage.sectionIsLocked,
    ]
  );

  const goToSectionPage = useCallback(
    () => history.push(`${navigatePath(history.location.pathname, -1)}`),
    [history]
  );

  const handleSubmit = async (
    updatedDynamicProperties: FieldsFromBM,
    formActions: FormikHelpers<FieldsFromBM>
  ) => {
    try {
      const cleanedUpdatedDynamicProperties =
        currentSectionStorage.currentSection.getDynamicPropertiesWithDecimalsAndDateFormatted(
          updatedDynamicProperties
        );
      let patchJson = diff(getInitialValues(), cleanedUpdatedDynamicProperties);
      await currentSectionStorage.editSectionDetails(patchJson);
      warningLeavePageService.setIsDirty(false);
      return goToSectionPage();
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withForm(updatedDynamicProperties, formActions);
      applicationError.withErrorMessage("default-error.edit");
      handleErrorInSnackbar(applicationError);
    }
  };

  const getInitialValues = useCallback(() => {
    return currentSectionStorage.currentSection.getDynamicPropertiesKeyValueForEdit();
  }, [currentSectionStorage.currentSection]);

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([{
      entity: currentSectionStorage.parentEntityFromPath.type,
      name: await currentSectionStorage.getParentEntityName(currentSectionStorage.currentSection.project.name)
      }, {
      entity: EntityTypeFromPath.SewerObject,
      name: currentSectionStorage.currentSection.name
      }
    ]);

    breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [currentSectionStorage, currentSectionStorage.currentSection, history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[EditSewerObject,EditSewerObjectFromAssignedProject]}
      titleKey="EditSection.title"
      titleContext={{ sectionName: currentSectionStorage.currentSection.name }}
      id="edit-section"
      menuItems={menuItems}
      isLoading={currentSectionStorage.isLoading}
    >
      <Form
        formikOptions={{
          initialValues: getInitialValues(),
          onSubmit: handleSubmit,
          validateOnBlur: false,
          validateOnChange: false,
          enableReinitialize: true,
        }}
        warningKey={getSectionIsLockedWarning()?.key}
        warningContext={getSectionIsLockedWarning()?.context}
        canSubmit={canSubmitEdit}
        onCancel={goToSectionPage}
      >
        {({ values, setFieldValue }) => {
          return (
            <>
              <DataSection labelKey="general.section.details">
                <SubSection>
                  <DisplaySewerObjectId
                    id={currentSectionStorage.currentSection.name}
                    labelKey="sewer-objects.DisplaySectionId.label"
                  />
                  <DisplayDataOwner dataOwner={currentSectionStorage.currentSection.dataOwner} />
                </SubSection>
                <SubSection>
                  <DisplayInProject project={currentSectionStorage.currentSection.project} />
                  <DisplayOpenJobs openJobs={currentSectionStorage.currentSection.openJobs} />
                </SubSection>
                <SubSection>
                  <DisplayDateCreated date={currentSectionStorage.currentSection.created} />
                  <DisplayCreatedBy
                    createdBy={currentSectionStorage.currentSection.createdBy.name}
                  />
                </SubSection>
              </DataSection>
              <EditDynamicProperties
                labelKey="general.characteristics"
                properties={currentSectionStorage.currentSection.getDynamicPropertiesForEdit()}
                values={values}
                setFieldValue={setFieldValue}
              />
            </>
          );
        }}
      </Form>
    </PageLayout>
  );
});
